import { useState } from "react";
import {
    Box, Typography, Grid, Avatar, Chip, useMediaQuery, Tabs, Tab
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { styled } from "@mui/material/styles";
import { AiFillBulb } from "react-icons/ai";

const PageWrapper = styled(Box)(() => ({
    minHeight: "100vh",
    background: "linear-gradient(160deg, #03020a 0%, #060c18 50%, #03050f 100%)",
    color: "#fff",
    fontFamily: "'JetBrains Mono','Fira Code','Courier New',monospace",
    paddingBottom: "60px",
    paddingTop: "32px",
}));

const SectionCard = styled(Box)(() => ({
    background: "rgba(26,14,52,0.95)",
    border: "1px solid rgba(218,165,32,0.25)",
    borderRadius: "18px",
    padding: "32px",
    marginBottom: "28px",
    position: "relative",
    overflow: "hidden",
    "&::before": {
        content: '""',
        position: "absolute",
        top: 0, left: "10%", right: "10%", height: "1px",
        background: "linear-gradient(to right,transparent,rgba(218,165,32,0.3),transparent)",
    },
}));

const MobileDataCard = styled(Box)(() => ({
    background: "rgba(255,255,255,0.03)",
    border: "1px solid rgba(218,165,32,0.15)",
    borderRadius: "12px",
    padding: "14px 16px",
    marginBottom: "10px",
}));

const CodeBlock = styled(Box)(() => ({
    background: "rgba(6,2,16,0.98)",
    border: "1px solid rgba(218,165,32,0.2)",
    borderRadius: "12px",
    padding: "20px 24px",
    fontFamily: "'JetBrains Mono','Fira Code','Courier New',monospace",
    fontSize: "0.82rem",
    lineHeight: 1.9,
    overflowX: "auto",
    whiteSpace: "pre",
    color: "#e6edf3",
    position: "relative",
    "&::before": {
        content: '"C++"',
        position: "absolute",
        top: "8px", right: "14px",
        fontSize: "9px",
        color: "rgba(218,165,32,0.5)",
        letterSpacing: "2px",
        fontWeight: 700,
    },
}));

const TableHeader = ({ columns, cols }) => (
    <Box sx={{
        display: "grid",
        gridTemplateColumns: cols || `repeat(${columns.length},1fr)`,
        gap: "12px", padding: "12px 16px",
        bgcolor: "rgba(218,165,32,0.1)", color: "goldenrod",
        fontWeight: "bold", fontSize: "0.78rem",
        letterSpacing: "0.06em", textTransform: "uppercase",
        borderBottom: "1px solid rgba(218,165,32,0.15)",
    }}>
        {columns.map(c => <div key={c}>{c}</div>)}
    </Box>
);

const SectionHeading = ({ icon, title, subtitle }) => (
    <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2, mt: 4, mb: subtitle ? 1 : 2 }}>
        <Avatar sx={{ bgcolor: "goldenrod", color: "#100720", width: 38, height: 38, flexShrink: 0, mt: 0.3 }}>
            {icon}
        </Avatar>
        <Box>
            <Typography variant="h6" fontWeight={700}>{title}</Typography>
            {subtitle && <Typography sx={{ fontSize: "0.84rem", color: "rgba(255,255,255,0.45)", mt: 0.3 }}>{subtitle}</Typography>}
        </Box>
    </Box>
);

const ConceptCard = ({ title, color = "goldenrod", children }) => (
    <Box sx={{
        bgcolor: "rgba(255,255,255,0.025)", border: `1px solid ${color}33`,
        borderLeft: `3px solid ${color}`, borderRadius: "12px",
        p: "16px 18px", mb: 2,
    }}>
        {title && <Typography sx={{ fontWeight: 700, color, fontSize: "0.82rem", mb: 1, textTransform: "uppercase", letterSpacing: "0.06em" }}>{title}</Typography>}
        {children}
    </Box>
);

const StyledTabs = ({ value, onChange, tabs }) => (
    <Tabs value={value} onChange={onChange} variant="scrollable" scrollButtons="auto"
          sx={{
              mb: 2, borderBottom: "1px solid rgba(255,255,255,0.1)",
              "& .MuiTab-root": { fontSize: "0.8rem", fontWeight: 600, color: "rgba(255,255,255,0.45)", textTransform: "none", minHeight: 40 },
              "& .Mui-selected": { color: "goldenrod !important" },
              "& .MuiTabs-indicator": { bgcolor: "goldenrod" },
          }}>
        {tabs.map((t, i) => <Tab key={i} label={t} />)}
    </Tabs>
);

const PillChip = ({ label, color = "goldenrod" }) => (
    <Chip label={label} size="small" sx={{
        bgcolor: `${color}18`, color, fontWeight: 700, fontSize: "0.72rem",
        height: 22, border: `1px solid ${color}44`, "& .MuiChip-label": { px: 1 },
    }} />
);
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// DATA — Section 1: try / catch / throw basics
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

const basicsTabs = [
    {
        label: "try / catch / throw",
        code: `#include <iostream>
#include <stdexcept>

// ── The three keywords ─────────────────────────────────────────
//   throw  — signals that something went wrong
//   try    — wraps code that might throw
//   catch  — handles a specific exception type

double divide(double a, double b) {
    if (b == 0.0)
        throw std::invalid_argument("Cannot divide by zero");
    //  ↑ throws: execution jumps OUT of divide() immediately
    return a / b;
}

int main() {
    try {
        // ── Code that might throw goes here ───────────────────
        double r1 = divide(10.0, 2.0);    // fine — returns 5.0
        std::cout << r1 << "\\n";

        double r2 = divide(10.0, 0.0);    // ← throws!
        std::cout << r2 << "\\n";          // ← NEVER reached

        std::cout << "After bad call\\n";   // ← NEVER reached
    }
    catch (const std::invalid_argument& e) {
        // ── Execution resumes here after the throw ────────────
        std::cerr << "Caught: " << e.what() << "\\n";
        // Output: Caught: Cannot divide by zero
    }
    std::cout << "Program continues normally\\n";
    // ↑ Execution continues here after the catch block
    return 0;
}`,
    },
    {
        label: "Multiple catch blocks",
        code: `#include <iostream>
#include <stdexcept>
#include <vector>

void riskyOperation(int choice) {
    if (choice == 1) throw std::invalid_argument("Bad argument");
    if (choice == 2) throw std::out_of_range("Out of range");
    if (choice == 3) throw std::runtime_error("Runtime failure");
    if (choice == 4) throw 42;              // throw int (unusual!)
    if (choice == 5) throw "literal error"; // throw C-string (avoid!)
}

int main() {
    try {
        riskyOperation(2);
    }
    // ── Catch blocks checked IN ORDER — most specific FIRST ───
    catch (const std::invalid_argument& e) {
        std::cerr << "Invalid arg: " << e.what() << "\\n";
    }
    catch (const std::out_of_range& e) {    // ← catches choice==2
        std::cerr << "Out of range: " << e.what() << "\\n";
    }
    catch (const std::runtime_error& e) {
        std::cerr << "Runtime error: " << e.what() << "\\n";
    }
    catch (const std::exception& e) {        // ← catches ANY std exception
        std::cerr << "std::exception: " << e.what() << "\\n";
    }
    catch (int code) {                       // ← catches thrown int
        std::cerr << "Integer error code: " << code << "\\n";
    }
    catch (...) {                            // ← catch-all: catches ANYTHING
        std::cerr << "Unknown exception caught\\n";
    }
    // NOTE: put catch(...) LAST — it matches everything!
}`,
    },
    {
        label: "Re-throwing",
        code: `#include <iostream>
#include <stdexcept>

void level3() {
    throw std::runtime_error("Deep error from level3");
}

void level2() {
    try {
        level3();
    }
    catch (const std::runtime_error& e) {
        std::cerr << "level2 caught: " << e.what() << "\\n";
        throw;  // ← re-throw the SAME exception (preserves type & message)
        // throw e; ← BAD: creates a copy, may slice the exception!
    }
}

void level1() {
    try {
        level2();
    }
    catch (const std::exception& e) {
        // ── Wrap in a new exception with more context ─────────
        throw std::runtime_error(
            std::string("level1 context: ") + e.what()
        );
    }
}

int main() {
    try {
        level1();
    }
    catch (const std::exception& e) {
        std::cerr << "Final handler: " << e.what() << "\\n";
        // Output: Final handler: level1 context: Deep error from level3
    }

    // ── std::current_exception / std::rethrow_exception ───────
    std::exception_ptr eptr;
    try { throw std::runtime_error("stored"); }
    catch (...) { eptr = std::current_exception(); }  // store it
    if (eptr) {
        try { std::rethrow_exception(eptr); }          // rethrow later
        catch (const std::exception& e) { std::cerr << e.what(); }
    }
}`,
    },
    {
        label: "noexcept",
        code: `#include <iostream>
#include <stdexcept>

// ── noexcept — declares a function will NOT throw ─────────────
// Allows compiler to generate more efficient code
// If it DOES throw, std::terminate() is called immediately!

int safeAdd(int a, int b) noexcept {
    return a + b;    // guaranteed not to throw
}

double safeDivide(double a, double b) noexcept(false) {  // may throw
    if (b == 0) throw std::domain_error("div by zero");
    return a / b;
}

// ── noexcept in move operations (crucial for performance) ──────
class Buffer {
    int* data;
    size_t size;
public:
    Buffer(size_t n) : data(new int[n]), size(n) {}

    // Move constructor MUST be noexcept for vector to use it!
    Buffer(Buffer&& other) noexcept
        : data(other.data), size(other.size) {
        other.data = nullptr;
        other.size = 0;
    }
    ~Buffer() { delete[] data; }
};

// ── noexcept operator — test at compile time ───────────────────
static_assert(noexcept(safeAdd(1, 2)));       // ✅ compiles
// static_assert(noexcept(safeDivide(1, 0))); // ❌ would fail

// ── Rule of thumb ─────────────────────────────────────────────
// Always mark noexcept: destructors, move constructors, move assignment
// Consider noexcept: swap functions, simple getters
// Don't mark noexcept: functions that call throwing code`,
    },
];

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// DATA — Section 2: Standard Exception Hierarchy
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

const stdExceptions = [
    {
        name: "std::exception",
        header: "<exception>",
        color: "#fbbf24",
        desc: "Base class for ALL standard exceptions. Provides what() method.",
        example: "catch (const std::exception& e) { e.what(); }",
        isBase: true,
    },
    {
        name: "std::logic_error",
        header: "<stdexcept>",
        color: "#f0883e",
        desc: "Programming errors detectable at compile time. Avoidable with correct logic.",
        example: "throw std::logic_error(\"precondition violated\");",
        children: [
            { name: "std::invalid_argument", desc: "Invalid argument passed to a function",            example: 'throw std::invalid_argument("age < 0");' },
            { name: "std::domain_error",     desc: "Math domain violation (sqrt(-1), log(0))",         example: 'throw std::domain_error("sqrt of negative");' },
            { name: "std::length_error",     desc: "Operation would exceed max allowed length",        example: 'throw std::length_error("string too long");' },
            { name: "std::out_of_range",     desc: "Index outside valid range. Thrown by at()",       example: 'throw std::out_of_range("index 99 > size 5");' },
        ],
    },
    {
        name: "std::runtime_error",
        header: "<stdexcept>",
        color: "#f85149",
        desc: "Errors that can only be detected at runtime. Hardware, I/O, network.",
        example: "throw std::runtime_error(\"file not found\");",
        children: [
            { name: "std::range_error",      desc: "Result outside representable range",               example: 'throw std::range_error("result overflow");' },
            { name: "std::overflow_error",   desc: "Arithmetic overflow",                              example: 'throw std::overflow_error("int overflow");' },
            { name: "std::underflow_error",  desc: "Arithmetic underflow",                             example: 'throw std::underflow_error("float underflow");' },
        ],
    },
    {
        name: "std::bad_alloc",
        header: "<new>",
        color: "#a855f7",
        desc: "Thrown by new when memory allocation fails (out of memory).",
        example: "int* p = new int[99999999]; // may throw bad_alloc",
        children: [],
    },
    {
        name: "std::bad_cast",
        header: "<typeinfo>",
        color: "#e879f9",
        desc: "Thrown by dynamic_cast when cast fails on a reference type.",
        example: "Base& b = ...; Derived& d = dynamic_cast<Derived&>(b);",
        children: [],
    },
    {
        name: "std::bad_typeid",
        header: "<typeinfo>",
        color: "#67e8f9",
        desc: "Thrown by typeid when applied to null pointer of polymorphic type.",
        example: "Base* p = nullptr; typeid(*p); // throws",
        children: [],
    },
];

const stdExcTabs = [
    {
        label: "logic_error family",
        code: `#include <stdexcept>
#include <string>

// ── std::logic_error and its subclasses ───────────────────────
// These represent PROGRAMMING BUGS — should be fixed, not caught!

void setAge(int age) {
    if (age < 0 || age > 150)
        throw std::invalid_argument("Age must be 0-150, got: " + std::to_string(age));
}

double mySqrt(double x) {
    if (x < 0)
        throw std::domain_error("sqrt undefined for negative: " + std::to_string(x));
    return std::sqrt(x);
}

std::string getElement(std::vector<std::string>& v, int i) {
    if (i < 0 || i >= (int)v.size())
        throw std::out_of_range(
            "Index " + std::to_string(i) +
            " out of range [0," + std::to_string(v.size()-1) + "]"
        );
    return v[i];
}

int main() {
    // ── vector::at() throws out_of_range automatically ────────
    std::vector<int> v = {1,2,3};
    try {
        v.at(10);   // throws std::out_of_range
    }
    catch (const std::out_of_range& e) {
        std::cerr << "Caught: " << e.what() << "\\n";
    }

    // ── string::at() throws out_of_range ─────────────────────
    std::string s = "Hello";
    try {
        s.at(100);  // throws std::out_of_range
    }
    catch (const std::out_of_range& e) {
        std::cerr << "String index error: " << e.what() << "\\n";
    }
}`,
    },
    {
        label: "runtime_error family",
        code: `#include <stdexcept>
#include <fstream>
#include <limits>

// ── std::runtime_error — errors you can't predict at compile time ─

void readFile(const std::string& path) {
    std::ifstream file(path);
    if (!file.is_open())
        throw std::runtime_error("Cannot open file: " + path);

    std::string line;
    while (std::getline(file, line))
        std::cout << line << "\\n";
}

int multiplyChecked(int a, int b) {
    // Detect overflow before it happens
    if (a != 0 && b > std::numeric_limits<int>::max() / a)
        throw std::overflow_error(
            "int overflow: " + std::to_string(a) + " * " + std::to_string(b)
        );
    return a * b;
}

// ── Custom runtime error ───────────────────────────────────────
class NetworkError : public std::runtime_error {
public:
    int statusCode;
    NetworkError(const std::string& msg, int code)
        : std::runtime_error(msg), statusCode(code) {}
};

int main() {
    try {
        readFile("nonexistent.txt");
    }
    catch (const std::runtime_error& e) {
        std::cerr << "Runtime: " << e.what() << "\\n";
    }

    try {
        throw NetworkError("Connection refused", 503);
    }
    catch (const NetworkError& e) {
        std::cerr << "HTTP " << e.statusCode << ": " << e.what() << "\\n";
    }
    catch (const std::runtime_error& e) {
        std::cerr << "Fallback: " << e.what() << "\\n";
    }
}`,
    },
    {
        label: "bad_alloc & memory",
        code: `#include <new>
#include <iostream>
#include <memory>

int main() {
    // ── std::bad_alloc — operator new throws when out of memory ─
    try {
        // Request absurdly large allocation
        int* p = new int[999999999999LL];  // likely throws
        delete[] p;
    }
    catch (const std::bad_alloc& e) {
        std::cerr << "Allocation failed: " << e.what() << "\\n";
    }

    // ── nothrow new — returns nullptr instead of throwing ─────
    int* p = new (std::nothrow) int[999999999999LL];
    if (p == nullptr) {
        std::cerr << "Allocation returned nullptr\\n";
    } else {
        delete[] p;
    }

    // ── set_new_handler — install a custom OOM callback ───────
    std::set_new_handler([]() {
        std::cerr << "Custom: out of memory!\\n";
        throw std::bad_alloc(); // must throw or terminate
    });

    // ── Smart pointers don't leak even on exception ────────────
    try {
        auto ptr = std::make_unique<int[]>(1000);
        throw std::runtime_error("something went wrong");
        // ptr is automatically freed even though we threw!
    }
    catch (const std::exception& e) {
        std::cerr << e.what() << "\\n";
        // No memory leak — unique_ptr destructor ran
    }
}`,
    },
];

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// DATA — Section 3: Custom Exceptions
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

const customExcTabs = [
    {
        label: "Basic Custom Exception",
        code: `#include <stdexcept>
#include <string>

// ── Inherit from std::exception or its subclasses ─────────────
// ALWAYS inherit from std::exception hierarchy — never throw raw types!

class AppException : public std::runtime_error {
public:
    explicit AppException(const std::string& msg)
        : std::runtime_error(msg) {}
};

// ── More detailed custom exception ────────────────────────────
class ValidationError : public std::invalid_argument {
    std::string field;
    std::string value;
public:
    ValidationError(const std::string& field,
                    const std::string& value,
                    const std::string& reason)
        : std::invalid_argument(
            "Validation failed for '" + field + "'" +
            " (value='" + value + "'): " + reason
          ),
          field(field), value(value) {}

    std::string getField() const { return field; }
    std::string getValue() const { return value; }
};

void validateEmail(const std::string& email) {
    if (email.find('@') == std::string::npos)
        throw ValidationError("email", email, "missing @ symbol");
    if (email.size() < 5)
        throw ValidationError("email", email, "too short");
}

int main() {
    try {
        validateEmail("notanemail");
    }
    catch (const ValidationError& e) {
        std::cerr << e.what() << "\\n";
        std::cerr << "Field: " << e.getField() << "\\n";
        // Output: Validation failed for 'email' (value='notanemail'): missing @
    }
    catch (const std::invalid_argument& e) {
        std::cerr << "Fallback: " << e.what() << "\\n";
    }
}`,
    },
    {
        label: "Exception Hierarchy",
        code: `#include <stdexcept>
#include <string>

// ── Build a full application exception hierarchy ──────────────
//
//  std::exception
//  └── AppException              (base for all app errors)
//      ├── DatabaseException
//      │   ├── ConnectionException
//      │   └── QueryException
//      └── HttpException
//          ├── ClientException   (4xx)
//          └── ServerException   (5xx)

class AppException : public std::runtime_error {
public:
    explicit AppException(const std::string& msg)
        : std::runtime_error("[App] " + msg) {}
    virtual ~AppException() = default;
};

class DatabaseException : public AppException {
public:
    explicit DatabaseException(const std::string& msg)
        : AppException("[DB] " + msg) {}
};

class ConnectionException : public DatabaseException {
    std::string host;
    int port;
public:
    ConnectionException(const std::string& host, int port)
        : DatabaseException("Cannot connect to " + host + ":" + std::to_string(port)),
          host(host), port(port) {}
    std::string getHost() const { return host; }
    int         getPort() const { return port; }
};

class HttpException : public AppException {
    int statusCode;
public:
    HttpException(int code, const std::string& msg)
        : AppException("[HTTP " + std::to_string(code) + "] " + msg),
          statusCode(code) {}
    int getStatus() const { return statusCode; }
};

int main() {
    try {
        throw ConnectionException("db.example.com", 5432);
    }
    catch (const ConnectionException& e) {        // most specific first
        std::cerr << "Conn failed: " << e.getHost() << ":" << e.getPort() << "\\n";
    }
    catch (const DatabaseException& e) {          // fallback
        std::cerr << "DB error: " << e.what() << "\\n";
    }
    catch (const AppException& e) {               // widest app catch
        std::cerr << "App error: " << e.what() << "\\n";
    }
    catch (const std::exception& e) {             // std library fallback
        std::cerr << "std error: " << e.what() << "\\n";
    }
}`,
    },
    {
        label: "what() override",
        code: `#include <exception>
#include <string>
#include <sstream>

// ── Inherit from std::exception directly (lowest level) ───────
// Must override what() to return a const char*

class RichException : public std::exception {
    std::string message;          // store formatted message here
    int         errorCode;
    std::string file;
    int         line;

public:
    RichException(const std::string& msg, int code,
                  const std::string& file, int line)
        : errorCode(code), file(file), line(line) {
        // Build a rich message in constructor
        std::ostringstream oss;
        oss << "[Error " << code << "] " << msg
            << " (at " << file << ":" << line << ")";
        message = oss.str();
    }

    // Override what() — must return pointer that stays valid!
    const char* what() const noexcept override {
        return message.c_str();  // c_str() is valid as long as message lives
    }

    int         getCode() const { return errorCode; }
    std::string getFile() const { return file; }
    int         getLine() const { return line; }
};

// ── Convenience macro to capture file/line ────────────────────
#define THROW_RICH(msg, code) \
    throw RichException((msg), (code), __FILE__, __LINE__)

int main() {
    try {
        THROW_RICH("Disk full", 507);
    }
    catch (const RichException& e) {
        std::cerr << e.what() << "\\n";
        std::cerr << "Code: " << e.getCode() << "\\n";
        // [Error 507] Disk full (at main.cpp:42)
    }
}`,
    },
];
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// DATA — Section 4: Stack Unwinding & RAII
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

const raiiTabs = [
    {
        label: "Stack Unwinding",
        code: `#include <iostream>
#include <stdexcept>

// ── Stack unwinding: destructors run in reverse order ─────────
// When an exception is thrown, C++ automatically destroys
// all LOCAL objects between the throw and the catch.

class Resource {
    std::string name;
public:
    Resource(const std::string& n) : name(n) {
        std::cout << "  [+] Acquired: " << name << "\\n";
    }
    ~Resource() {
        std::cout << "  [-] Released: " << name << "\\n";
    }
};

void inner() {
    Resource r3("r3-inner");        // acquired last
    std::cout << "  About to throw...\\n";
    throw std::runtime_error("Something failed!");
    // r3 destructor runs BEFORE exception propagates
}

void middle() {
    Resource r2("r2-middle");       // acquired second
    inner();
    // r2 destructor also runs during unwinding
}

int main() {
    std::cout << "Starting...\\n";
    try {
        Resource r1("r1-main");     // acquired first
        middle();
        // ↑ throws — r1, r2, r3 all destroyed in reverse order
    }
    catch (const std::runtime_error& e) {
        std::cout << "Caught: " << e.what() << "\\n";
    }
    // Output:
    //   [+] Acquired: r1-main
    //   [+] Acquired: r2-middle
    //   [+] Acquired: r3-inner
    //   About to throw...
    //   [-] Released: r3-inner   ← reverse order!
    //   [-] Released: r2-middle
    //   [-] Released: r1-main
    //   Caught: Something failed!
}`,
    },
    {
        label: "RAII & Exceptions",
        code: `#include <fstream>
#include <memory>
#include <mutex>
#include <stdexcept>

// ── RAII guarantees cleanup even on exceptions ────────────────

// ❌ BAD — manual cleanup leaks on exception:
void badVersion() {
    FILE* fp = fopen("data.txt", "r");
    int*  buf = new int[100];

    process(fp, buf);       // ← if this throws...
    // these lines never run! Memory leak + file stays open:
    delete[] buf;
    fclose(fp);
}

// ✅ GOOD — RAII: resources tied to object lifetime:
void goodVersion() {
    std::ifstream file("data.txt");     // RAII: auto-closes on destruction
    auto buf = std::make_unique<int[]>(100);  // RAII: auto-frees

    process(file, buf.get());           // if THIS throws...
    // Destructors still run! file closed + buf freed automatically.
}

// ── std::lock_guard — RAII mutex management ───────────────────
std::mutex mtx;

void threadSafeOperation() {
    std::lock_guard<std::mutex> lock(mtx);  // RAII: locks on creation
    // ... do thread-safe work ...
    throw std::runtime_error("error in critical section");
    // lock released automatically — mutex NEVER left locked!
}   // ← lock_guard destructor unlocks mtx here

// ── Custom RAII guard ─────────────────────────────────────────
class ScopedTimer {
    std::string name;
    std::chrono::high_resolution_clock::time_point start;
public:
    ScopedTimer(std::string n) : name(n),
        start(std::chrono::high_resolution_clock::now()) {}
    ~ScopedTimer() {
        auto end = std::chrono::high_resolution_clock::now();
        auto ms = std::chrono::duration_cast<std::chrono::milliseconds>(end-start).count();
        std::cout << name << " took " << ms << "ms\\n";
        // Always prints — even if an exception was thrown
    }
};`,
    },
    {
        label: "Exception Safety Levels",
        code: `// ── The Four Exception Safety Guarantees ─────────────────────
//
//  Level 4: No-throw guarantee (strongest)
//    - Function NEVER throws. Marked noexcept.
//    - Example: swap(), destructor, move operations
//
//  Level 3: Strong guarantee (commit-or-rollback)
//    - Either succeeds completely OR throws and leaves
//      the object in its ORIGINAL state (no partial changes)
//    - "Transactional": all-or-nothing
//
//  Level 2: Basic guarantee (minimum for correct code)
//    - If it throws, invariants are maintained and no
//      resources are leaked — but state may have changed
//
//  Level 1: No guarantee (broken — avoid!)
//    - May leave object in inconsistent state on exception

// ── Strong guarantee example: copy-and-swap idiom ─────────────
class SafeVector {
    int* data;
    size_t size;
public:
    // Copy assignment with STRONG guarantee:
    SafeVector& operator=(const SafeVector& other) {
        if (this == &other) return *this;

        // 1. Allocate NEW memory (may throw — original unchanged)
        int* newData = new int[other.size];

        // 2. Copy data (may throw — original still unchanged)
        std::copy(other.data, other.data + other.size, newData);

        // 3. Commit — noexcept from here:
        delete[] data;        // free old (noexcept)
        data = newData;       // swap pointers (noexcept)
        size = other.size;    // update size (noexcept)
        return *this;
    }
    // If step 1 or 2 throws: old data untouched ✅ Strong guarantee
};

// ── Basic guarantee example ───────────────────────────────────
void appendToFile(std::vector<std::string>& lines, const std::string& path) {
    // May throw at any point — but no leaks, no corruption
    std::ofstream file(path, std::ios::app);  // RAII
    for (const auto& line : lines)
        file << line << "\\n";                 // may throw (disk full)
    // BASIC guarantee: file handle freed, no leak — but some lines
    // may or may not have been written to disk.
}`,
    },
    {
        label: "Destructor Rules",
        code: `#include <iostream>
#include <stdexcept>

// ── RULE: NEVER throw from a destructor ───────────────────────
// If a destructor throws WHILE another exception is propagating,
// std::terminate() is called immediately — your program crashes!

class DangerousDestructor {
public:
    ~DangerousDestructor() {
        // ❌ NEVER do this:
        // throw std::runtime_error("Destructor threw!");
        // ↑ If another exception is already active → std::terminate!
    }
};

// ── SAFE: catch all exceptions inside the destructor ──────────
class SafeDestructor {
    std::string filename;
public:
    SafeDestructor(const std::string& f) : filename(f) {}
    ~SafeDestructor() noexcept {  // ← always mark noexcept!
        try {
            cleanup(filename);    // might throw internally
        }
        catch (const std::exception& e) {
            // Log but don't propagate:
            std::cerr << "Cleanup error (ignored): " << e.what() << "\\n";
        }
        catch (...) {
            std::cerr << "Unknown cleanup error (ignored)\\n";
        }
        // ← exception is SWALLOWED — destructor exits normally
    }
};

// ── Summary of destructor rules ───────────────────────────────
//  ✅ Always mark destructors noexcept (it's the default in C++11)
//  ✅ Catch all exceptions inside destructors
//  ✅ Log errors — don't silently swallow them
//  ❌ Never let an exception escape a destructor`,
    },
];

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// DATA — Section 5: Best Practices & Patterns
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

const patternTabs = [
    {
        label: "When to Use Exceptions",
        code: `// ── Use exceptions for EXCEPTIONAL conditions ────────────────
// Exceptions should signal errors that the caller
// cannot reasonably be expected to prevent.

// ✅ GOOD uses of exceptions:
//   - File not found / disk full / network unreachable
//   - Out-of-memory (std::bad_alloc)
//   - Invalid config at startup
//   - Failed database transaction
//   - Constraint violation that shouldn't happen in correct code

// ❌ BAD uses of exceptions:
//   - Normal control flow (don't throw to return early)
//   - Expected "not found" cases (return optional/nullptr)
//   - Frequent code paths (exceptions have overhead)
//   - Simple validation in tight loops

// ── Prefer error codes for EXPECTED failures ──────────────────
#include <optional>
#include <variant>

// ✅ Return optional for "might not exist" cases:
std::optional<User> findUser(int id) {
    if (!db.has(id)) return std::nullopt;  // expected — no throw
    return db.get(id);
}

// ✅ Return variant<T, Error> for recoverable errors:
std::variant<double, std::string> safeDivide(double a, double b) {
    if (b == 0) return "Division by zero";  // return error as value
    return a / b;
}

// ✅ Throw for truly exceptional / programming error:
double mustDivide(double a, double b) {
    if (b == 0)
        throw std::invalid_argument("b must be non-zero (bug in caller)");
    return a / b;
}`,
    },
    {
        label: "Exception Patterns",
        code: `#include <stdexcept>
#include <functional>
#include <optional>

// ── Pattern 1: translate exceptions at boundaries ─────────────
// Convert low-level exceptions to your domain's exception type
class DataService {
public:
    User getUser(int id) {
        try {
            return database.query("SELECT ... WHERE id=" + std::to_string(id));
        }
        catch (const sql::SQLException& e) {
            // Translate SQL exception to app-level exception:
            throw DatabaseException("Failed to fetch user " + std::to_string(id)
                                    + ": " + e.what());
        }
    }
};

// ── Pattern 2: retry with exponential backoff ─────────────────
template<typename Fn>
auto withRetry(Fn&& fn, int maxAttempts = 3) {
    for (int attempt = 1; attempt <= maxAttempts; ++attempt) {
        try {
            return fn();
        }
        catch (const NetworkException& e) {
            if (attempt == maxAttempts) throw;  // rethrow on last attempt
            std::this_thread::sleep_for(
                std::chrono::milliseconds(100 * (1 << attempt))  // exponential backoff
            );
        }
    }
}

// ── Pattern 3: scope guard (execute on scope exit) ────────────
class ScopeGuard {
    std::function<void()> cleanup;
    bool dismissed = false;
public:
    ScopeGuard(std::function<void()> fn) : cleanup(fn) {}
    ~ScopeGuard() { if (!dismissed) cleanup(); }
    void dismiss() { dismissed = true; }  // cancel cleanup
};

void transactionalOperation() {
    beginTransaction();
    ScopeGuard guard([](){ rollback(); });  // rollback unless we dismiss
    doWork();                               // if this throws → rollback!
    guard.dismiss();                        // success — don't rollback
    commit();
}`,
    },
    {
        label: "Error Handling Comparison",
        code: `// ── C++ error handling strategies — when to use each ─────────

// ── 1. Exceptions (std::exception hierarchy) ──────────────────
// Best for: exceptional/unexpected errors, constructors,
//           cross-library boundaries, deep call stacks
double parseDouble(const std::string& s) {
    try { return std::stod(s); }
    catch (const std::invalid_argument&) {
        throw std::invalid_argument("Not a number: " + s);
    }
}

// ── 2. std::optional — value or nothing ───────────────────────
// Best for: "not found", optional results, simple lookups
#include <optional>
std::optional<int> findIndex(const std::vector<int>& v, int target) {
    for (int i=0; i<(int)v.size(); ++i)
        if (v[i] == target) return i;
    return std::nullopt;  // no throw — absence is expected
}
// Usage: if (auto idx = findIndex(v, 42)) { use *idx; }

// ── 3. std::variant<T, Error> — value or error ────────────────
// Best for: recoverable errors, functional style, no overhead
#include <variant>
struct ParseError { std::string msg; };
std::variant<double, ParseError> tryParseDouble(const std::string& s) {
    try { return std::stod(s); }
    catch (...) { return ParseError{"Not a number: " + s}; }
}
// Usage: visit overload on the variant

// ── 4. Error codes — C compatibility, real-time systems ───────
// Best for: C API, real-time/embedded, no exceptions allowed
enum class ErrCode { Ok, NotFound, InvalidArg, IOError };
ErrCode readConfig(const std::string& path, Config& out) {
    if (!exists(path)) return ErrCode::NotFound;
    // ...
    return ErrCode::Ok;
}`,
    },
];

// Cheatsheet data
const cheatBlocks = [
    {
        title: "throw / try / catch Rules", color: "#f0883e",
        items: [
            "✅ Always catch by const reference: catch(const T& e)",
            "✅ Catch most specific exception FIRST, base class LAST",
            "✅ Use bare 'throw;' to re-throw, not 'throw e;'",
            "✅ catch(...) must be the LAST catch block",
            "✅ Always inherit custom exceptions from std::exception",
            "❌ Never throw from destructors — use noexcept",
            "❌ Never throw raw types (int, char*, etc.)",
            "❌ Never use exceptions for normal control flow",
        ],
    },
    {
        title: "noexcept Rules", color: "#58a6ff",
        items: [
            "✅ Mark destructors noexcept (default in C++11)",
            "✅ Mark move constructors/assignment noexcept",
            "✅ Mark swap() functions noexcept",
            "✅ Mark simple getters noexcept",
            "✅ Use noexcept(false) to explicitly allow throwing",
            "❌ Don't mark noexcept if the function can throw",
            "❌ If noexcept function throws: std::terminate() called!",
        ],
    },
    {
        title: "RAII & Safety", color: "#7ee787",
        items: [
            "✅ Use std::unique_ptr/shared_ptr for heap resources",
            "✅ Use std::lock_guard/unique_lock for mutexes",
            "✅ Use std::fstream (not fopen) for files",
            "✅ Aim for Strong guarantee in public APIs",
            "✅ Use copy-and-swap for strong exception safety",
            "❌ Never use raw new/delete — smart pointers instead",
            "❌ Never manually unlock a mutex — use lock_guard",
        ],
    },
    {
        title: "Common Mistakes", color: "#f85149",
        items: [
            "❌ Catching by value: catch(std::exception e) — slices!",
            "❌ Re-throwing with 'throw e' — copies, may slice",
            "❌ Swallowing exceptions silently: catch(...) {}",
            "❌ Throwing in destructors — undefined behaviour",
            "❌ Using errno/C error codes when exceptions exist",
            "❌ Logic errors in catch block that throw again",
            "❌ Forgetting to check e.what() in catch — wasted info",
        ],
    },
];

// Exception safety levels
const safetyLevels = [
    { level: "No-throw",   symbol: "4️⃣", color: "#56d364", desc: "Never throws. noexcept.", example: "swap(), destructor, move ops" },
    { level: "Strong",     symbol: "3️⃣", color: "#fbbf24", desc: "Commit-or-rollback — original state preserved on failure.", example: "copy-and-swap, transactional ops" },
    { level: "Basic",      symbol: "2️⃣", color: "#f0883e", desc: "Invariants maintained, no leaks — but state may change.", example: "Most std:: containers" },
    { level: "No guarantee",symbol:"1️⃣", color: "#f85149", desc: "May leave object broken on exception. Avoid!", example: "Legacy C code" },
];
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// MAIN COMPONENT
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

export default function CppExceptionHandling() {
    const theme    = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

    const [basicsTab,  setBasicsTab]  = useState(0);
    const [stdExcTab,  setStdExcTab]  = useState(0);
    const [customTab,  setCustomTab]  = useState(0);
    const [raiiTab,    setRaiiTab]    = useState(0);
    const [patternTab, setPatternTab] = useState(0);

    return (
        <PageWrapper>

            {/* ── Page Header ── */}
            <Box sx={{ textAlign: "center", mb: 5, px: 2 }}>
                <Typography variant={isMobile ? "h5" : "h3"} sx={{
                    fontWeight: 800, color: "goldenrod", display: "inline-block",
                    px: { xs: 3, md: 6 }, py: 1.5, borderRadius: "999px",
                    boxShadow: "0 6px 20px rgba(218,165,32,0.3)",
                    border: "1px solid rgba(218,165,32,0.25)", letterSpacing: "0.04em",
                }}>
                    C++ EXCEPTION HANDLING
                </Typography>
                <Typography sx={{ mt: 2, color: "rgba(255,255,255,0.45)", fontSize: "0.88rem" }}>
                    try · catch · throw · noexcept · RAII · Custom Exceptions · Best Practices
                </Typography>
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, justifyContent: "center", mt: 2 }}>
                    {["try/catch/throw","std::exception","Custom Exceptions","Stack Unwinding","RAII","noexcept"].map(t => (
                        <Chip key={t} label={t} size="small" sx={{
                            bgcolor: "rgba(218,165,32,0.1)", color: "rgba(218,165,32,0.85)",
                            border: "1px solid rgba(218,165,32,0.25)", fontSize: "0.72rem",
                        }} />
                    ))}
                </Box>
            </Box>

            {/* ── Intro Banner ── */}
            <Box sx={{ px: { xs: 2, md: 6 }, mb: 4 }}>
                <Box sx={{ bgcolor: "rgba(218,165,32,0.06)", border: "1px solid rgba(218,165,32,0.2)", borderRadius: "14px", p: "18px 24px", display: "flex", gap: 2, alignItems: "flex-start" }}>
                    <Typography sx={{ fontSize: "1.4rem", flexShrink: 0 }}>💡</Typography>
                    <Typography sx={{ fontSize: "0.9rem", lineHeight: 1.85, color: "rgba(255,255,255,0.8)" }}>
                        <strong style={{ color: "goldenrod" }}>Exception handling</strong> lets C++ programs detect and
                        recover from errors at runtime using <code style={{ color: "goldenrod" }}>try</code>,{" "}
                        <code style={{ color: "goldenrod" }}>catch</code>, and <code style={{ color: "goldenrod" }}>throw</code>.
                        Combined with <strong style={{ color: "goldenrod" }}>RAII</strong>, it guarantees that
                        resources are always released — even when errors occur — making code both
                        safe and maintainable.
                    </Typography>
                </Box>
            </Box>

            <Grid container spacing={4} sx={{ px: { xs: 2, md: 6 } }}>
                <Grid item xs={12}>

                    {/* ════════════════════════════════════════════════════
              SECTION 1 — try / catch / throw BASICS
          ════════════════════════════════════════════════════ */}
                    <SectionCard>
                        <Typography variant="h5" fontWeight={800} color="goldenrod" mb={2}>
                            1. try / catch / throw
                        </Typography>
                        <Typography mb={3} sx={{ opacity: 0.88, lineHeight: 1.85 }}>
                            When something goes wrong, <strong>throw</strong> an exception. Wrap the risky
                            code in <strong>try</strong>. Handle the error in a matching <strong>catch</strong> block.
                            Execution jumps directly from the throw to the first matching catch — skipping
                            all code in between.
                        </Typography>

                        <ConceptCard title="Execution Flow Visualised" color="#f0883e">
                            <CodeBlock>{`//  Normal flow (no exception):
//    main() → try block → divide() → return value → continue after try
//
//  Exception flow:
//    main() → try block → divide() → THROW!
//              ↓                       ↓
//         execution jumps ────────→ catch block
//                                      ↓
//                              resumes after catch
//
//  Three keywords:
//    throw expr;               ← signal the error (can be any type)
//    try { ... }               ← guard the risky code
//    catch (const Type& e) { } ← handle the error
//
//  Key rule: catch blocks checked IN ORDER — most specific type first!
//  Base class catches ALSO catch all derived exceptions.`}</CodeBlock>
                        </ConceptCard>

                        <StyledTabs value={basicsTab} onChange={(_, v) => setBasicsTab(v)} tabs={basicsTabs.map(t => t.label)} />
                        <CodeBlock>{basicsTabs[basicsTab].code}</CodeBlock>

                        {/* Catch patterns grid */}
                        <SectionHeading icon={<AiFillBulb size={18} />} title="catch Block Patterns" subtitle="Order matters — always catch most specific first" />
                        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                            {[
                                { pattern: "catch (const SpecificType& e)",  color: "#56d364", rule: "Most specific — catches only that exact type (and derived classes)", code: "catch (const std::out_of_range& e) { ... }" },
                                { pattern: "catch (const std::exception& e)",color: "#fbbf24", rule: "Catches ALL standard library exceptions — use as fallback", code: "catch (const std::exception& e) { std::cerr << e.what(); }" },
                                { pattern: "catch (const BaseClass& e)",     color: "#f0883e", rule: "Catches derived exception types via polymorphism", code: "catch (const std::runtime_error& e) { ... }" },
                                { pattern: "catch (...)",                    color: "#f85149", rule: "LAST resort: catches absolutely anything. No access to exception object!", code: "catch (...) { std::cerr << \"Unknown error\\n\"; }" },
                            ].map((row, i) => (
                                <Box key={i} sx={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr 1fr", gap: "12px", bgcolor: "rgba(255,255,255,0.025)", border: `1px solid ${row.color}22`, borderLeft: `3px solid ${row.color}`, borderRadius: "12px", px: 2, py: "12px", alignItems: "center" }}>
                                    <Typography sx={{ fontFamily: "monospace", fontWeight: 700, color: row.color, fontSize: "0.8rem" }}>{row.pattern}</Typography>
                                    <Typography sx={{ fontSize: "0.81rem", opacity: 0.78, lineHeight: 1.6 }}>{row.rule}</Typography>
                                    <Typography sx={{ fontFamily: "monospace", color: "#a5d6ff", fontSize: "0.77rem" }}>{row.code}</Typography>
                                </Box>
                            ))}
                        </Box>
                    </SectionCard>

                    {/* ════════════════════════════════════════════════════
              SECTION 2 — STANDARD EXCEPTION HIERARCHY
          ════════════════════════════════════════════════════ */}
                    <SectionCard>
                        <Typography variant="h5" fontWeight={800} color="goldenrod" mb={2}>
                            2. Standard Exception Hierarchy
                        </Typography>
                        <Typography mb={3} sx={{ opacity: 0.88, lineHeight: 1.85 }}>
                            All standard library exceptions inherit from{" "}
                            <code style={{ color: "goldenrod" }}>std::exception</code>.
                            This hierarchy lets you catch broad categories (any{" "}
                            <code style={{ color: "goldenrod" }}>std::runtime_error</code>) or specific ones (only{" "}
                            <code style={{ color: "goldenrod" }}>std::out_of_range</code>).
                        </Typography>

                        {/* Visual hierarchy */}
                        <ConceptCard title="Full std::exception Hierarchy" color="#fbbf24">
                            <CodeBlock>{`// std::exception                          ← #include <exception>
// ├── std::logic_error                    ← #include <stdexcept>
// │   ├── std::invalid_argument           ← bad function argument
// │   ├── std::domain_error               ← math domain violation
// │   ├── std::length_error               ← exceeds max length
// │   └── std::out_of_range               ← index/value out of range
// │
// ├── std::runtime_error                  ← #include <stdexcept>
// │   ├── std::range_error                ← result out of range
// │   ├── std::overflow_error             ← arithmetic overflow
// │   └── std::underflow_error            ← arithmetic underflow
// │
// ├── std::bad_alloc                      ← #include <new>
// ├── std::bad_cast                       ← #include <typeinfo>
// ├── std::bad_typeid                     ← #include <typeinfo>
// ├── std::bad_exception                  ← #include <exception>
// └── std::bad_function_call              ← #include <functional>
//
// Key method: e.what()  → returns const char* describing the error`}</CodeBlock>
                        </ConceptCard>

                        {/* Exception cards */}
                        <SectionHeading icon={<span style={{ fontSize: 18 }}>📚</span>} title="Exception Types Quick Reference" />
                        <Box sx={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: 2, mb: 3 }}>
                            {stdExceptions.map((exc, i) => (
                                <Box key={i} sx={{ bgcolor: "rgba(255,255,255,0.025)", border: `1px solid ${exc.color}33`, borderTop: `3px solid ${exc.color}`, borderRadius: "12px", p: "14px 16px" }}>
                                    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 0.75 }}>
                                        <Typography sx={{ fontFamily: "monospace", fontWeight: 800, color: exc.color, fontSize: "0.85rem" }}>{exc.name}</Typography>
                                        <PillChip label={exc.header} color={exc.color} />
                                    </Box>
                                    <Typography sx={{ fontSize: "0.8rem", opacity: 0.72, mb: exc.children?.length ? 1 : 0, lineHeight: 1.6 }}>{exc.desc}</Typography>
                                    {exc.children?.length > 0 && (
                                        <Box sx={{ borderLeft: `2px solid ${exc.color}44`, pl: 1.5 }}>
                                            {exc.children.map((child, j) => (
                                                <Box key={j} sx={{ mb: j < exc.children.length - 1 ? 0.5 : 0 }}>
                                                    <Typography sx={{ fontFamily: "monospace", color: exc.color, fontSize: "0.76rem", fontWeight: 600, opacity: 0.9 }}>↳ {child.name}</Typography>
                                                    <Typography sx={{ fontSize: "0.75rem", opacity: 0.6, lineHeight: 1.5 }}>{child.desc}</Typography>
                                                </Box>
                                            ))}
                                        </Box>
                                    )}
                                </Box>
                            ))}
                        </Box>

                        <StyledTabs value={stdExcTab} onChange={(_, v) => setStdExcTab(v)} tabs={stdExcTabs.map(t => t.label)} />
                        <CodeBlock>{stdExcTabs[stdExcTab].code}</CodeBlock>
                    </SectionCard>

                    {/* ════════════════════════════════════════════════════
              SECTION 3 — CUSTOM EXCEPTIONS
          ════════════════════════════════════════════════════ */}
                    <SectionCard>
                        <Typography variant="h5" fontWeight={800} color="goldenrod" mb={2}>
                            3. Custom Exceptions
                        </Typography>
                        <Typography mb={3} sx={{ opacity: 0.88, lineHeight: 1.85 }}>
                            Create your own exception types to carry domain-specific information — error codes,
                            field names, HTTP status codes, file paths — anything the caller needs to
                            handle the error properly. Always inherit from the{" "}
                            <code style={{ color: "goldenrod" }}>std::exception</code> hierarchy.
                        </Typography>

                        <ConceptCard title="Rules for Custom Exceptions" color="#a855f7">
                            <Box sx={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: 2 }}>
                                {[
                                    { icon: "✅", color: "#56d364", text: "Inherit from std::exception or a subclass (runtime_error, logic_error)" },
                                    { icon: "✅", color: "#56d364", text: "Pass a descriptive message to the base class constructor" },
                                    { icon: "✅", color: "#56d364", text: "Override what() if your class stores its own message string" },
                                    { icon: "✅", color: "#56d364", text: "Mark what() override as noexcept — it must not throw" },
                                    { icon: "❌", color: "#f85149", text: "Don't throw raw int, string, or plain struct (not catchable by std handlers)" },
                                    { icon: "❌", color: "#f85149", text: "Don't store non-trivial members without virtual destructor in base" },
                                ].map((rule, j) => (
                                    <Box key={j} sx={{ display: "flex", gap: 1 }}>
                                        <Typography sx={{ fontSize: "1rem", flexShrink: 0 }}>{rule.icon}</Typography>
                                        <Typography sx={{ fontSize: "0.81rem", opacity: 0.78, lineHeight: 1.65 }}>{rule.text}</Typography>
                                    </Box>
                                ))}
                            </Box>
                        </ConceptCard>

                        <StyledTabs value={customTab} onChange={(_, v) => setCustomTab(v)} tabs={customExcTabs.map(t => t.label)} />
                        <CodeBlock>{customExcTabs[customTab].code}</CodeBlock>
                    </SectionCard>

                    {/* ════════════════════════════════════════════════════
              SECTION 4 — STACK UNWINDING & RAII
          ════════════════════════════════════════════════════ */}
                    <SectionCard>
                        <Typography variant="h5" fontWeight={800} color="goldenrod" mb={2}>
                            4. Stack Unwinding &amp; RAII
                        </Typography>
                        <Typography mb={3} sx={{ opacity: 0.88, lineHeight: 1.85 }}>
                            When an exception propagates, C++ automatically runs the destructors of all
                            local objects between the throw and the catch — this is called{" "}
                            <strong>stack unwinding</strong>. <strong>RAII</strong> (Resource Acquisition Is
                            Initialisation) exploits this guarantee to ensure resources are always released.
                        </Typography>

                        <StyledTabs value={raiiTab} onChange={(_, v) => setRaiiTab(v)} tabs={raiiTabs.map(t => t.label)} />
                        <CodeBlock>{raiiTabs[raiiTab].code}</CodeBlock>

                        {/* Exception safety levels */}
                        <SectionHeading icon={<span style={{ fontSize: 18 }}>🛡️</span>} title="Exception Safety Levels" subtitle="Every public API should declare its guarantee" />
                        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                            {safetyLevels.map((row, i) => (
                                <Box key={i} sx={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "100px 1fr 1fr", gap: "12px", bgcolor: "rgba(255,255,255,0.025)", border: `1px solid ${row.color}33`, borderLeft: `3px solid ${row.color}`, borderRadius: "12px", px: 2, py: "12px", alignItems: "center" }}>
                                    <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                                        <Typography sx={{ fontSize: "1.1rem" }}>{row.symbol}</Typography>
                                        <Typography sx={{ fontWeight: 800, color: row.color, fontSize: "0.82rem" }}>{row.level}</Typography>
                                    </Box>
                                    <Typography sx={{ fontSize: "0.81rem", opacity: 0.8, lineHeight: 1.6 }}>{row.desc}</Typography>
                                    <Typography sx={{ fontFamily: "monospace", color: "#a5d6ff", fontSize: "0.78rem", opacity: 0.85 }}>{row.example}</Typography>
                                </Box>
                            ))}
                        </Box>

                        {/* RAII resources table */}
                        <SectionHeading icon={<span style={{ fontSize: 18 }}>🔧</span>} title="RAII Wrappers for Common Resources" />
                        <Box sx={{ bgcolor: "rgba(255,255,255,0.03)", borderRadius: "12px", overflow: "hidden", border: "1px solid rgba(255,255,255,0.06)" }}>
                            {!isMobile && <TableHeader columns={["Resource", "Raw (Unsafe)", "RAII Wrapper (Safe)", "Header"]} cols="120px 1fr 1fr 120px" />}
                            {[
                                { res:"Heap memory",   raw:"int* p = new int;",         raii:"std::unique_ptr<int>",          hdr:"<memory>" },
                                { res:"Shared heap",   raw:"int* p = new int;",         raii:"std::shared_ptr<int>",          hdr:"<memory>" },
                                { res:"File",          raw:"FILE* fp = fopen(...);",    raii:"std::fstream / std::ifstream",  hdr:"<fstream>" },
                                { res:"Mutex",         raw:"mtx.lock(); ... mtx.unlock();", raii:"std::lock_guard<mutex>",   hdr:"<mutex>" },
                                { res:"Timed mutex",   raw:"mtx.lock(); ... mtx.unlock();", raii:"std::unique_lock<mutex>",  hdr:"<mutex>" },
                                { res:"Dynamic array", raw:"int* a = new int[n];",      raii:"std::vector<int>",             hdr:"<vector>" },
                                { res:"Thread",        raw:"pthread_t (C-style)",        raii:"std::jthread (C++20)",         hdr:"<thread>" },
                            ].map((row, i) =>
                                isMobile ? (
                                    <MobileDataCard key={i}>
                                        <Typography sx={{ fontWeight: 700, color: "goldenrod", fontSize: "0.85rem", mb: 0.5 }}>{row.res}</Typography>
                                        <Typography sx={{ fontFamily: "monospace", color: "#f85149", fontSize: "0.76rem", mb: 0.3 }}>❌ {row.raw}</Typography>
                                        <Typography sx={{ fontFamily: "monospace", color: "#56d364", fontSize: "0.76rem" }}>✅ {row.raii}</Typography>
                                    </MobileDataCard>
                                ) : (
                                    <Box key={i} sx={{ display: "grid", gridTemplateColumns: "120px 1fr 1fr 120px", gap: "12px", px: 2, py: "10px", borderBottom: "1px solid rgba(255,255,255,0.05)", alignItems: "center", fontSize: "0.82rem", "&:last-child": { borderBottom: "none" }, "&:hover": { bgcolor: "rgba(218,165,32,0.04)" } }}>
                                        <Box sx={{ fontWeight: 700, color: "goldenrod", fontSize: "0.8rem" }}>{row.res}</Box>
                                        <Box sx={{ fontFamily: "monospace", color: "#f85149", fontSize: "0.78rem" }}>❌ {row.raw}</Box>
                                        <Box sx={{ fontFamily: "monospace", color: "#56d364", fontSize: "0.78rem" }}>✅ {row.raii}</Box>
                                        <PillChip label={row.hdr} color="#a5d6ff" />
                                    </Box>
                                )
                            )}
                        </Box>
                    </SectionCard>

                    {/* ════════════════════════════════════════════════════
              SECTION 5 — BEST PRACTICES & PATTERNS
          ════════════════════════════════════════════════════ */}
                    <SectionCard>
                        <Typography variant="h5" fontWeight={800} color="goldenrod" mb={2}>
                            5. Best Practices &amp; Patterns
                        </Typography>
                        <Typography mb={3} sx={{ opacity: 0.88, lineHeight: 1.85 }}>
                            Exceptions are powerful — but overusing them or using them incorrectly makes code
                            harder to reason about. These patterns show when to throw, when to use
                            alternatives, and how to structure exception handling across a codebase.
                        </Typography>

                        <StyledTabs value={patternTab} onChange={(_, v) => setPatternTab(v)} tabs={patternTabs.map(t => t.label)} />
                        <CodeBlock>{patternTabs[patternTab].code}</CodeBlock>

                        {/* Performance note */}
                        <ConceptCard title="Performance Characteristics" color="#67e8f9">
                            <Box sx={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr 1fr", gap: 2 }}>
                                {[
                                    { icon: "✅", color: "#56d364", title: "Happy path: zero cost", body: "When no exception is thrown, try blocks have essentially zero overhead in modern compilers (zero-cost EH model)." },
                                    { icon: "⚠️", color: "#fbbf24", title: "Throwing is expensive", body: "Stack unwinding, RTTI lookup, and object construction make throwing exceptions ~10-100x slower than normal returns." },
                                    { icon: "💡", color: "#58a6ff", title: "When to use alternatives", body: "For expected failures in tight loops: use std::optional, error codes, or std::expected (C++23) to avoid the throw cost." },
                                ].map((card, i) => (
                                    <Box key={i} sx={{ bgcolor: "rgba(255,255,255,0.03)", border: `1px solid ${card.color}33`, borderRadius: "10px", p: "12px 14px" }}>
                                        <Box sx={{ display: "flex", gap: 1, alignItems: "center", mb: 0.5 }}>
                                            <Typography sx={{ fontSize: "1rem" }}>{card.icon}</Typography>
                                            <Typography sx={{ fontWeight: 700, color: card.color, fontSize: "0.81rem" }}>{card.title}</Typography>
                                        </Box>
                                        <Typography sx={{ fontSize: "0.79rem", opacity: 0.7, lineHeight: 1.65 }}>{card.body}</Typography>
                                    </Box>
                                ))}
                            </Box>
                        </ConceptCard>
                    </SectionCard>

                    {/* ════════════════════════════════════════════════════
              CHEATSHEET
          ════════════════════════════════════════════════════ */}
                    <SectionCard>
                        <Typography variant="h5" fontWeight={800} color="goldenrod" mb={3}>
                            Exception Handling Cheatsheet
                        </Typography>
                        <Box sx={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: 3 }}>
                            {cheatBlocks.map((block, i) => (
                                <Box key={i} sx={{ bgcolor: "rgba(255,255,255,0.025)", border: `1px solid ${block.color}33`, borderRadius: "14px", p: "18px 20px" }}>
                                    <Typography sx={{ fontWeight: 800, color: block.color, fontSize: "0.88rem", mb: 1.5, textTransform: "uppercase", letterSpacing: "0.05em" }}>{block.title}</Typography>
                                    <Box sx={{ display: "flex", flexDirection: "column", gap: 0.65 }}>
                                        {block.items.map((item, j) => (
                                            <Typography key={j} sx={{ fontSize: "0.81rem", opacity: 0.82, lineHeight: 1.6 }}>{item}</Typography>
                                        ))}
                                    </Box>
                                </Box>
                            ))}
                        </Box>

                        {/* Quick syntax reference */}
                        <Box sx={{ mt: 4 }}>
                            <Typography sx={{ fontWeight: 800, color: "goldenrod", fontSize: "0.88rem", mb: 2, textTransform: "uppercase", letterSpacing: "0.05em" }}>
                                Syntax Quick Reference
                            </Typography>
                            <Box sx={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: 2 }}>
                                {[
                                    { title: "Throw", color: "#f85149",  code: `throw std::runtime_error("msg");
throw std::invalid_argument("bad arg");
throw MyException("custom", 404);` },
                                    { title: "Try/Catch", color: "#58a6ff", code: `try {
    riskyCode();
} catch (const Specific& e) {
    handle(e.what());
} catch (const std::exception& e) {
    fallback(e.what());
} catch (...) { /* last resort */ }` },
                                    { title: "Re-throw", color: "#fbbf24", code: `catch (const std::exception& e) {
    log(e.what());
    throw;        // ✅ bare throw preserves type
    // throw e;   // ❌ may slice!
}` },
                                    { title: "noexcept", color: "#7ee787", code: `void safe()    noexcept;        // never throws
void risky()   noexcept(false); // may throw
~MyClass()     noexcept;        // destructors
MyClass(MyClass&&) noexcept;    // move ops` },
                                ].map((item, i) => (
                                    <Box key={i} sx={{ bgcolor: "rgba(255,255,255,0.025)", border: `1px solid ${item.color}33`, borderRadius: "12px", overflow: "hidden" }}>
                                        <Box sx={{ bgcolor: `${item.color}12`, px: 2, py: 1, borderBottom: `1px solid ${item.color}22` }}>
                                            <Typography sx={{ fontWeight: 700, color: item.color, fontSize: "0.82rem" }}>{item.title}</Typography>
                                        </Box>
                                        <CodeBlock sx={{ borderRadius: 0, border: "none", fontSize: "0.79rem", p: "12px 16px", "&::before": { content: '""' } }}>
                                            {item.code}
                                        </CodeBlock>
                                    </Box>
                                ))}
                            </Box>
                        </Box>
                    </SectionCard>

                </Grid>
            </Grid>
        </PageWrapper>
    );
}
